import { useMemo, useState } from 'react';
import { Copy, ExternalLink, Link2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { adminApi } from '@/services/adminApi';

type AgencyPlanId = 'agency_starter' | 'agency_medium' | 'agency_professional';

const TIER_BANDS: Record<AgencyPlanId, { min: number; max: number; label: string }> = {
  agency_starter: { min: 1, max: 5, label: 'Agency Starter' },
  agency_medium: { min: 6, max: 10, label: 'Agency Medium' },
  agency_professional: { min: 11, max: 20, label: 'Agency Professional' },
};

const DEFAULT_SUCCESS_URL = 'https://hiromatch.com/profile?tab=billing&checkout=success';

interface Props {
  uid: string;
  userEmail: string;
}

export function AgencyUpgradeLinkCard({ uid, userEmail }: Props) {
  const [agencyPlanId, setAgencyPlanId] = useState<AgencyPlanId>('agency_starter');
  const [desiredCompanies, setDesiredCompanies] = useState('1');
  const [cancelSmeAtPeriodEnd, setCancelSmeAtPeriodEnd] = useState(true);
  const [successUrl, setSuccessUrl] = useState(DEFAULT_SUCCESS_URL);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [lastWarnings, setLastWarnings] = useState<string[]>([]);

  const band = TIER_BANDS[agencyPlanId];

  const qtyNum = useMemo(() => {
    const n = parseInt(desiredCompanies, 10);
    return Number.isFinite(n) ? n : NaN;
  }, [desiredCompanies]);

  const qtyValid = Number.isFinite(qtyNum) && qtyNum >= band.min && qtyNum <= band.max;

  const generate = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (!qtyValid) {
      toast.error(`Companies must be between ${band.min} and ${band.max} for ${band.label}`);
      return;
    }
    setBusy(true);
    setCheckoutUrl(null);
    setLastWarnings([]);
    try {
      const res = await adminApi.users.agencyCheckoutLink(uid, {
        agencyPlanId,
        desiredCompanies: qtyNum,
        successUrl: successUrl.trim() || undefined,
        cancelSmeAtPeriodEnd,
        reason: reason.trim(),
      });
      setCheckoutUrl(res.checkoutUrl);
      setLastWarnings(res.warnings ?? []);
      if (res.checkoutUrl) {
        toast.success('Checkout link ready — copy and send to the user');
      } else {
        toast.warning('No checkout URL returned — see warnings below');
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!checkoutUrl) return;
    try {
      await navigator.clipboard.writeText(checkoutUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  return (
    <Card className="border-border/50 border-dashed">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-2">
          <Link2 className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <h2 className="text-sm font-semibold">Agency upgrade link</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Generate a Stripe Checkout URL for {userEmail}. The user pays on their account (
              <span className="font-mono">ownerUid</span>); webhook promotes to agency after success.
              Do not use Edit → Agency owner for billing — use this link instead.
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Agency plan</Label>
            <Select
              value={agencyPlanId}
              onValueChange={(v) => {
                const plan = v as AgencyPlanId;
                setAgencyPlanId(plan);
                const b = TIER_BANDS[plan];
                const n = parseInt(desiredCompanies, 10);
                if (!Number.isFinite(n) || n < b.min) setDesiredCompanies(String(b.min));
                if (Number.isFinite(n) && n > b.max) setDesiredCompanies(String(b.max));
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agency_starter">Agency Starter (1–5 companies)</SelectItem>
                <SelectItem value="agency_medium">Agency Medium (6–10)</SelectItem>
                <SelectItem value="agency_professional">Agency Professional (11–20)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Number of companies (seats)</Label>
            <Input
              type="number"
              min={band.min}
              max={band.max}
              value={desiredCompanies}
              onChange={(e) => setDesiredCompanies(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Allowed for {band.label}: {band.min}–{band.max}
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Checkout success URL</Label>
          <Input value={successUrl} onChange={(e) => setSuccessUrl(e.target.value)} />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="cancel-sme"
            checked={cancelSmeAtPeriodEnd}
            onCheckedChange={(v) => setCancelSmeAtPeriodEnd(v === true)}
          />
          <Label htmlFor="cancel-sme" className="text-sm font-normal cursor-pointer">
            Schedule SME subscription to cancel at period end (keeps paid days, avoids SME renewal)
          </Label>
        </div>

        <div className="space-y-1.5">
          <Label>Reason <span className="text-destructive">*</span></Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you generating this link?"
            rows={2}
          />
        </div>

        <Button onClick={() => void generate()} disabled={busy || !qtyValid}>
          {busy ? 'Generating…' : 'Generate checkout link'}
        </Button>

        {lastWarnings.length > 0 && (
          <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1 list-disc pl-4">
            {lastWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}

        {checkoutUrl && (
          <div className="rounded-md border bg-muted/40 p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Send this link to the user</p>
            <p className="text-xs font-mono break-all">{checkoutUrl}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => void copyLink()}>
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" asChild>
                <a href={checkoutUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" /> Open
                </a>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
